import { useMemo } from 'react';

import { Grid as Grid2 } from '@mui/material';

import { gql } from '@graphql/__generated__';
import type { OrganisationScoreCardsFragment } from '@graphql/__generated__/graphql';
import { ScoreCard, ScoreCardSkeleton } from '@modules/teams/TeamScoreCards';
import { useLocale } from '@shared/hooks/locale';

export const ORGANISATION_SCORE_CARDS_FRAGMENT = gql(/* GraphQL */ `
  fragment OrganisationScoreCards on organisations {
    id
    teams {
      id
      teams_users {
        user {
          id
          broker_leads_aggregate(distinct_on: [lead_id]) {
            aggregate {
              count
            }
          }
          lots_aggregate {
            aggregate {
              count
            }
          }
          property_transactions_aggregate {
            aggregate {
              sum {
                purchase_price
              }
            }
          }
        }
      }
    }
  }
`);

export const OrganisationScoreCards: React.FC<{
  organisation?: OrganisationScoreCardsFragment | null;
  loading: boolean;
}> = ({ organisation, loading }) => {
  const { t } = useLocale();
  const cards = useMemo(() => {
    if (organisation == null) {
      return [];
    }

    const totalLeads = organisation.teams.reduce(
      (acc, team) =>
        acc +
        team.teams_users.reduce(
          (acc, tu) =>
            acc + (tu.user?.broker_leads_aggregate.aggregate?.count || 0),
          0,
        ),
      0,
    );

    const totalLots = organisation.teams.reduce(
      (acc, team) =>
        acc +
        team.teams_users.reduce(
          (acc, tu) => acc + (tu.user?.lots_aggregate.aggregate?.count || 0),
          0,
        ),
      0,
    );

    const totalSales = organisation.teams.reduce(
      (acc, team) =>
        acc +
        team.teams_users.reduce(
          (acc, tu) =>
            acc +
            (tu.user?.property_transactions_aggregate.aggregate?.sum
              ?.purchase_price || 0),
          0,
        ),
      0,
    );

    return [
      {
        title: t('Teams'),
        value: organisation.teams.length,
      },
      {
        title: t('Leads'),
        value: totalLeads,
      },
      {
        title: t('Listings'),
        value: totalLots,
      },
      {
        title: t('Sales'),
        value: Intl.NumberFormat().format(totalSales || 0),
      },
    ];
  }, [organisation, t]);

  if (loading) {
    return (
      <Grid2 container spacing={2}>
        {Array.from({ length: 4 }).map((_, i) => (
          <Grid2 size={{ xs: 6, sm: 4, lg: 2.4 }} key={i}>
            <ScoreCardSkeleton />
          </Grid2>
        ))}
      </Grid2>
    );
  }

  return (
    <Grid2 container spacing={2} rowSpacing={0.5}>
      {cards.map(({ title, value }) => (
        <Grid2 size={{ xs: 6, sm: 4, lg: 2.4 }} key={title}>
          <ScoreCard title={title} value={value} />
        </Grid2>
      ))}
    </Grid2>
  );
};
